import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  Injector,
  OnDestroy,
  TemplateRef,
  ViewContainerRef,
  afterNextRender,
  effect,
  inject,
  input,
  model,
  untracked,
  viewChild,
} from '@angular/core';
import { deepActiveElement, focusInitial, restoreFocus, trapTabKey } from '../../modal/focus-trap';
import { TitleIfTruncatedDirective } from '../card/title-if-truncated.directive';

let nextId = 0;

/**
 * A side panel for details, opened over the page with a backdrop. Esc, the
 * backdrop and the corner close button close it; Tab stays inside while it is
 * open and focus returns to the opener afterwards. Slots: `[drawerMeta]`
 * (badges under the title), `[drawerActions]` (top right), the body, and
 * `[drawerFooter]`. An element marked `data-autofocus` receives focus on open.
 */
@Component({
  selector: 'app-drawer-cabinet',
  templateUrl: './drawer.component.html',
  styleUrl: './drawer.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TitleIfTruncatedDirective],
})
export class DrawerCabinetComponent implements OnDestroy {
  private readonly overlay = inject(Overlay);
  private readonly viewContainerRef = inject(ViewContainerRef);
  private readonly injector = inject(Injector);
  private readonly destroyRef = inject(DestroyRef);

  readonly open = model(false);
  readonly heading = input.required<string>();
  /** Small label above the title: "Project Details". */
  readonly eyebrow = input<string>('');
  /** 32rem or 48rem; full width on narrow screens. */
  readonly width = input<'md' | 'lg'>('lg');
  readonly closeLabel = input('Close');

  readonly titleId = `drawer-title-${nextId++}`;

  private readonly panelTemplate = viewChild.required<TemplateRef<unknown>>('panel');
  private readonly panel = viewChild<ElementRef<HTMLElement>>('panelEl');

  private overlayRef: OverlayRef | null = null;
  private opener: HTMLElement | null = null;

  constructor() {
    effect(() => {
      const isOpen = this.open();
      untracked(() => (isOpen ? this.attach() : this.detach()));
    });
    this.destroyRef.onDestroy(() => this.detach());
  }

  ngOnDestroy(): void {
    this.overlayRef?.dispose();
    this.overlayRef = null;
  }

  close(): void {
    this.open.set(false);
  }

  onKeydown(event: KeyboardEvent): void {
    const panel = this.panel()?.nativeElement;
    if (panel) trapTabKey(event, panel);
  }

  private attach(): void {
    if (this.overlayRef?.hasAttached()) return;
    this.opener = deepActiveElement();
    this.overlayRef ??= this.createOverlay();
    this.overlayRef.attach(new TemplatePortal(this.panelTemplate(), this.viewContainerRef));
    afterNextRender(
      () => {
        const panel = this.panel()?.nativeElement;
        if (panel) focusInitial(panel, panel);
      },
      { injector: this.injector },
    );
  }

  private detach(): void {
    if (!this.overlayRef?.hasAttached()) return;
    this.overlayRef.detach();
    restoreFocus(this.opener);
    this.opener = null;
  }

  private createOverlay(): OverlayRef {
    const ref = this.overlay.create({
      hasBackdrop: true,
      backdropClass: 'if-drawer-backdrop',
      panelClass: 'if-drawer-pane',
      positionStrategy: this.overlay.position().global().top('0').right('0'),
      scrollStrategy: this.overlay.scrollStrategies.block(),
      height: '100%',
      disposeOnNavigation: true,
    });
    ref.backdropClick().subscribe(() => this.close());
    ref.keydownEvents().subscribe((event) => {
      // A dialog opened from the drawer answers Esc itself; the drawer stays.
      if (event.key === 'Escape' && !event.defaultPrevented && !document.querySelector('.if-modal')) {
        event.preventDefault();
        this.close();
      }
    });
    ref.detachments().subscribe(() => {
      if (this.open()) this.open.set(false);
    });
    return ref;
  }
}
